<template>
    <transition
        name="fade-transition"
        mode="out-in"
        appear
    >
        <v-layout
            v-if="loading"
            justify-center
            key="load-state"
        >
            <v-progress-circular
                :size="60"
                :width="6"
                indeterminate
                class="my-3"
            />
        </v-layout>
        <v-layout
            class="subheading my-1"
            v-else-if="showDetails"
            key="rental-details"
        >
            <v-flex xs4>
                <v-layout v-if="!isAccountsReceivable">
                    <v-flex xs7 class="ml-2 my-1">Current Amount Due:</v-flex>
                    <v-flex data-testid="amount-due">
                        ${{ ledger.total_amount_due ? parseFloat(ledger.total_amount_due).toFixed(2) : "0.00" }}
                    </v-flex>
                </v-layout>
                <v-layout v-if="isAccountsReceivable">
                    <v-flex xs7 class="ml-2 my-1">Original Balance:</v-flex>
                    <v-flex data-testid="original-balance" v-if="isPlans">
                        ${{ ledger.total_amount_due ? parseFloat(ledger.total_amount_due).toFixed(2) : "0.00" }}
                    </v-flex>
                    <v-flex data-testid="original-balance" v-else>
                        ${{ parseFloat(promiseToPay.payment_amount).toFixed(2) }}
                    </v-flex>
                </v-layout>
                <v-layout v-if="!isAccountsReceivable">
                    <v-flex xs7 class="ml-2 my-1">Paid Through:</v-flex>
                    <v-flex data-testid="paid-through">{{ formatDate(ledger.paid_thru) }}</v-flex>
                </v-layout>
                <v-layout v-if="isPlans">
                    <v-flex xs7 class="ml-2 my-1">Next Scheduled Payment:</v-flex>
                    <v-flex v-if="isPlans">{{ formatDate(paymentPlan.start_date) }}</v-flex>
                </v-layout>
                <div v-if="!isAccountsReceivable">
                    <v-layout>
                        <v-flex xs7 class="ml-2 my-1">Scheduled Through:</v-flex>
                        <v-flex>{{ formatDate(ledger.scheduled_through) }}</v-flex>
                    </v-layout>
                    <v-layout>
                        <v-flex xs7 class="ml-2 my-1">Moved In:</v-flex>
                        <v-flex data-testid="moved-in">{{ formatDate(ledger.moved_in) }}</v-flex>
                    </v-layout>
                    <v-layout>
                        <v-flex xs7 class="ml-2 my-1">Auction Date:</v-flex>
                        <v-flex data-testid="auction-date">
                            <span class="error--text" v-if="!ledger.auction_date">No Auction Date</span>
                            <span class="success--text" v-else>{{ formatDate(ledger.auction_date) }}</span>
                        </v-flex>
                    </v-layout>
                    <v-layout>
                        <v-flex xs7 class="ml-2 my-1">TPP Premium:</v-flex>
                        <v-flex data-testid="tpp-premium">
                            ${{ ledger.insurance_premium ? parseFloat(ledger.insurance_premium).toFixed(2) : "0.00" }}
                        </v-flex>
                    </v-layout>
                    <v-layout>
                        <v-flex xs7 class="ml-2 my-1">Autobill Status:</v-flex>
                        <v-flex data-testid="autobill-status">
                            <span class="error--text" v-if="ledger.autobill === 0">Disabled</span>
                            <span class="success--text" v-else>Enabled</span>
                        </v-flex>
                    </v-layout>
                </div>
                <div v-if="isAccountsReceivable">
                    <v-layout>
                        <v-flex xs7 class="ml-2 my-1">Plan Status:</v-flex>
                        <v-flex data-testid="plan-status">
                            <span :class="statusCode(paymentPlan.status)" v-if="isPlans" style="text-transform: capitalize">
                                {{ paymentPlan.status }}
                            </span>
                            <span :class="statusCode(promiseToPay.status)" style="text-transform: capitalize" v-else>
                                {{ promiseToPay.status }}
                            </span>
                        </v-flex>
                    </v-layout>
                    <v-layout>
                        <v-flex xs7 class="ml-2 my-1">Plan Created:</v-flex>
                        <v-flex data-testid="plan-created">
                            <span v-if="isPlans">{{ formatDate(paymentPlan.created_at) }}</span>
                            <span v-else>{{ formatDate(promiseToPay.created_at) }}</span>
                        </v-flex>
                    </v-layout>
                    <div v-if="isPlans">
                        <v-layout>
                            <v-flex xs7 class="ml-2 my-1">Plan Duration:</v-flex>
                            <v-flex data-testid="plan-duration">
                                <span>{{ paymentPlan.duration }} Months</span>
                            </v-flex>
                        </v-layout>
                        <v-layout>
                            <v-flex xs7 class="ml-2 my-1">Monthly Rate:</v-flex>
                            <v-flex data-testid="plan-monthly-rate">
                                <span>${{ parseFloat(paymentPlan.monthly_rate).toFixed(2)  }}/month</span>
                            </v-flex>
                        </v-layout>
                    </div>
                    <div v-else>
                        <v-layout>
                            <v-flex xs7 class="ml-2 my-1">Payment Date:</v-flex>
                            <v-flex data-testid="promise-date">
                                <span>{{ formatDate(promiseToPay.payment_date) }}</span>
                            </v-flex>
                        </v-layout>
                        <v-layout>
                            <v-flex xs7 class="ml-2 my-1">Payment Attempts:</v-flex>
                            <v-flex data-testid="promise-attempts">
                                <span>{{ promiseToPay.payment_attempt }}</span>
                            </v-flex>
                        </v-layout>
                    </div>
                </div>
            </v-flex>
            <v-flex xs4 v-if="unitFeatures">
                <v-layout v-if="!isAccountsReceivable">
                    <v-flex xs5 class="ml-2 my-1">Unit Name:</v-flex>
                    <v-flex data-testid="unit-name">{{ unitFeatures.type_name }}</v-flex>
                </v-layout>
                <v-layout v-if="isAccountsReceivable">
                    <v-flex xs5 class="ml-2 my-1">Unit Number:</v-flex>
                    <v-flex data-testid="unit-name">{{ unitFeatures.number }}</v-flex>
                </v-layout>
                <v-layout>
                    <v-flex xs5 class="ml-2 my-1">Unit Type:</v-flex>
                    <v-flex data-testid="unit-type">{{ unitFeatures.product_type }}</v-flex>
                </v-layout>
                <v-layout>
                    <v-flex xs5 class="ml-2 my-1">Unit Size:</v-flex>
                    <v-flex data-testid="unit-size">
                        <span v-if="parseFloat(unitFeatures.width)">
                            '{{ parseFloat(unitFeatures.width) }} x '{{ parseFloat(unitFeatures.length) }}
                        </span>
                        <span v-else>'{{ parseFloat(unitFeatures.length) }}</span>
                    </v-flex>
                </v-layout>
                <v-layout v-if="!isAccountsReceivable">
                    <v-flex xs5 class="ml-2 my-1">Unit Features:</v-flex>
                    <v-flex>
                        <v-layout column>
                            <v-flex
                                v-for="(feature, i) in unitFeatures.features"
                                :key="i"
                                data-testid="unit-features"
                            >
                                {{ feature }}
                            </v-flex>
                        </v-layout>
                    </v-flex>
                </v-layout>
            </v-flex>
            <v-flex xs4>
                <v-layout>
                    <v-flex xs5 class="ml-2 my-1">Credit Card:</v-flex>
                    <v-flex data-testid="credit-card" v-if="!isAccountsReceivable">
                        <span v-if="!ledger.cc_last_four && !ledger.cc_type">N/A</span>
                        <span v-if="ledger.cc_type && ledger.cc_last_four">
                            {{ ledger.cc_type }} (...{{ ledger.cc_last_four }})
                        </span>
                    </v-flex>
                    <v-flex data-testid="credit-card" v-else>
                        <span v-if="(!paymentPlan || !paymentPlan.payments) && (!promiseToPay || !promiseToPay.card || !promiseToPay.card.last_4)">N/A</span>
                        <span v-if="paymentPlan && paymentPlan.payments && paymentPlan.payments[0] && paymentPlan.payments[0].card && paymentPlan.payments[0].card.last_4">
                        {{ translateCreditCardId(paymentPlan.payments[0].card.type) }} (...{{ paymentPlan.payments[0].card.last_4 }})
                    </span>
                        <span v-if="promiseToPay && promiseToPay.card && promiseToPay.card.last_4">
                        {{ translateCreditCardId(promiseToPay.card.type) }} (...{{ promiseToPay.card.last_4 }})
                    </span>
                    </v-flex>
                </v-layout>
                <v-layout v-if="!isAccountsReceivable">
                    <v-flex xs5 class="ml-2 my-1">Credit Card Exp:</v-flex>
                    <v-flex data-testid="credit-card-expiration">
                        <span v-if="!ledger.cc_exp">N/A</span>
                        <span v-else>{{ formatCCExp(ledger.cc_exp) }}</span>
                    </v-flex>
                </v-layout>
            </v-flex>
        </v-layout>
    </transition>
</template>

<script>
import { getCreditCardTypeFromId } from "@/helpers/sitelink";
import { mapState } from "vuex";
import moment from "moment";

export default {
  name: "TenantRentalDetails",
  props: {
    unitFeatures: {
      type: [Object, Array]
    },
    ledger: {
      type: [Object, String]
    },
    loading: {
      type: Boolean,
      default: false
    },
    paymentPlan: {
      type: [Object, String]
    },
    promiseToPay: {
      type: [Object, String]
    },
    isPlans: {
      type: Boolean
    },
    isPromise: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    formatDate(date) {
      return new moment.utc(date).format("MM/DD/YYYY");
    },

    formatCCExp(date) {
      return date.replace("-", "/");
    },

    translateCreditCardId(id) {
      return getCreditCardTypeFromId(id);
    },

    statusCode(status) {
      if (status) {
        if (status === "active" && this.darkMode) {
          return "white--text";
        } else if (status === "active" && !this.darkMode) {
          return "black--text";
        } else if (status === "pending") {
          return "yellow--text";
        } else if (status === "completed") {
          return "success--text";
        } else {
          return "error--text";
        }
      }
    }
  },

  computed: {
    ...mapState({
      darkMode: state => state.application.settings.darkTheme
    }),

    isAccountsReceivable() {
      return this.isPromise || this.isPlans;
    },

    showDetails() {
      if (this.isPlans && this.paymentPlan) {
        return this.paymentPlan.unit.name === this.unitFeatures.number;
      } else if (this.isPromise && this.promiseToPay) {
        return this.promiseToPay.unit.name === this.unitFeatures.number;
      } else if (!this.isPlans && !this.isPromise) {
        return this.ledger.facility_unit.name === this.unitFeatures.number;
      }

      return false;
    }
  }
};
</script>
